import { Box } from "@chakra-ui/react";
import React, { useEffect, useRef } from "react";

import { space } from "theme/themeConfig";
import { ErrorBoundary } from "react-error-boundary";
import {
  setBannerNotifications,
  useNotifications
} from "@shared/utils.notifications";
import { useHasMounted } from "utils/wrappers";
import type { UserNotificationType } from "@shared/graphql.types";

import { useRouter } from "lib/router";
import { Notification } from "./Notification";
import { NotificationFallback } from "./NotificationFallback";

const useShouldShowBanner = (notifications: UserNotificationType[]) => {
  const hasMounted = useHasMounted();

  const hasNotifications = notifications.length > 0;

  return hasNotifications && hasMounted;
};

export const NotificationsBanner: React.FC = () => {
  const notificationsBannerRef = useRef<HTMLDivElement | null>(null);
  const { bannerNotifications: notifications } = useNotifications();

  const { asPath } = useRouter();
  const shouldShowBanner = useShouldShowBanner(notifications);

  useEffect(() => {
    setBannerNotifications(
      notifications.filter(
        (n) => n.handler === "access_pass" && asPath === "/basket"
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath]);

  useEffect(() => {
    if (!notificationsBannerRef.current) return;

    notificationsBannerRef.current.scrollIntoView({
      block: "center",
      behavior: "smooth"
    });
  }, [notifications]);

  if (!shouldShowBanner) return null;

  const removeNotification = (code: string) =>
    notifications &&
    setBannerNotifications(
      notifications.filter((notification) => notification.code !== code)
    );

  return (
    <ErrorBoundary FallbackComponent={() => <NotificationFallback />}>
      <Box ref={notificationsBannerRef} data-testid="notifications-banner">
        {notifications?.map((n) => (
          <Box p={[space["-3"], space[1]]} key={n.code}>
            <Notification
              notification={n}
              removeNotification={removeNotification}
            />
          </Box>
        ))}
      </Box>
    </ErrorBoundary>
  );
};
